import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  ConfirmationMessageModal,
  FullScreenLoader,
  MenuItem,
  ScriptsTable,
  ScrollToTop,
  TableActionsMenu,
  PrintLayout,
} from '@Components/index';
import { CommonConstants } from '@Constants/index';
import ScriptsModal from '@Containers/Scripts/ScriptsModal';
import { MetaData } from '@Models/common.models';
import { GlobalScriptsQuery } from '@Models/scripts.models';
import {
  useDeleteGlobalScriptsMutation,
  useGetGlobalScriptsListsQuery,
} from '@Services/globalScripts.service';
import {
  BaseAlertProps,
  ConfirmationActionTypes,
  DialerCrudTypes,
  DialerTypes,
} from '@Typings/global.types';
import {
  GetRows,
  getRTKQuery,
  getModalBtnComponent,
  getResponseData,
  getConfirmationMessageNode,
  getFilters,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import usePrint from '@Utils/hooks/usePrint.hook';

const AdminScriptsActive: FunctionComponent = (): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAlert } = useAlert();
  const { componentRef, handlePrint, setPrintState } = usePrint(true);

  /** STATES */
  const [checked, setChecked] = useState<readonly string[]>([]);
  const [modalCrudType, setModalCrudType] = useState<DialerCrudTypes | null>(
    null
  );
  const [scriptId, setScriptId] = useState<string>('');
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });
  const [itemsCount, setItemsCount] = useState<number>(0);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [confirmationActionType, setConfirmationActionType] =
    useState<ConfirmationActionTypes | null>(null);
  const [isBulkAction, setIsBulkAction] = useState(false);

  const globalScriptsActiveQuery: GlobalScriptsQuery = useMemo(
    () => getRTKQuery(location.search),
    [location.search]
  );

  /** Page Heading */
  const pageHeading = t('scripts.Global Scripts');

  /** RTK QUERY HOOK */
  const {
    data,
    isLoading,
    isFetching,
    error: getScriptsError,
  } = useGetGlobalScriptsListsQuery({
    data: globalScriptsActiveQuery,
  });

  const [deleteGlobalScripts, deleteGlobalMethods] =
    useDeleteGlobalScriptsMutation();

  /** TABLE LOADING STATE */
  const loading = isFetching || deleteGlobalMethods.isLoading;

  // show error for get query
  useEffect(() => {
    if (getScriptsError) {
      triggerAlert(getScriptsError, setAlertState);
    }
  }, [getScriptsError, triggerAlert]);

  /** UPDATES THE CHECKED STATE AFTER BULK ACTIONS */
  useEffect(() => {
    setScriptId('');
    setIsBulkAction(false);
    setItemsCount(0);

    if (deleteGlobalMethods.isSuccess) {
      setChecked([]);
    }
  }, [deleteGlobalMethods.isSuccess]);

  /** TRIGGERS SUCCESS OR ERROR ALERT AFTER ACTIONS */
  useEffect(() => {
    // DELETE SUCCESS
    if (deleteGlobalMethods.isSuccess) {
      triggerAlert(
        CommonConstants.GLOBAL_MESSAGES.DELETE_SCRIPT,
        setAlertState
      );
    }

    // DELETE ERROR
    if (deleteGlobalMethods.error) {
      triggerAlert(deleteGlobalMethods.error, setAlertState);
    }
  }, [triggerAlert, deleteGlobalMethods.isSuccess, deleteGlobalMethods.error]);

  /** SCRIPTS ACTION HANDLERS --> */
  /** HANDLES SCRIPTS DELETE */
  const handleDelete = (ids: readonly string[]) => {
    const userId = query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID) || '';
    const metaData: MetaData = getFilters(query.parse());

    return itemsCount
      ? deleteGlobalScripts({ ...metaData, itemsCount, userId: `${userId}` })
      : deleteGlobalScripts({ ids, userId: `${userId}` });
  };

  /** HANDLES SCRIPT ADD */
  const handleScriptAdd = () => {
    setModalCrudType(DialerCrudTypes.Add);
    setScriptId('');
  };
  /** <-- SCRIPTS ACTION HANDLERS */

  /** Table Modal Button Component Function
   *
   * Returns the Modal Button component which opens modal when clicked.
   */
  const modalBtnComponent = (id: string, name: string): JSX.Element => {
    const handleClick = () => {
      setModalCrudType(DialerCrudTypes.View);
      setScriptId(id);
    };

    return getModalBtnComponent({
      id,
      name,
      onClick: handleClick,
      isAdmin: true,
    });
  };

  /** Handles confirmation click of modal and performs action accordingly */
  const handleConfirmationModalClick = () => {
    setIsConfirmationModalOpen(false);
    if (confirmationActionType === ConfirmationActionTypes.Delete) {
      return isBulkAction ? handleDelete(checked) : handleDelete([scriptId]);
    }
  };

  /** Table Actions Component Function
   *
   * Returns the table actions component with required menu items.
   */
  const actionsComponent = (id: string, disabled?: boolean): JSX.Element => {
    /** Handles click of action menu items */
    const handleTableActionsClick = (
      confirmationType: ConfirmationActionTypes
    ) => {
      setIsConfirmationModalOpen(true);
      setConfirmationActionType(confirmationType);
    };

    /** Actions Menu Data */
    const actionsMenu: MenuItem[] = [
      {
        label: t('common.messages.Edit'),
        icon: 'edit',
        value: id,
        onClick: () => {
          setModalCrudType(DialerCrudTypes.Edit);
          setScriptId(id);
        },
      },
      {
        label: t('common.messages.Delete'),
        icon: 'delete',
        value: id,
        onClick: () => {
          setItemsCount(0);
          setIsBulkAction(false);
          setScriptId(id);
          handleTableActionsClick(ConfirmationActionTypes.Delete);
        },
      },
      {
        label: t('common.messages.Print'),
        icon: 'print',
        value: id,
        onClick: () => {
          setPrintState(true);
          if (scriptId === id) {
            handlePrint();
          } else {
            setScriptId(id);
          }
        },
      },
    ];

    return <TableActionsMenu menuItems={actionsMenu} disabled={disabled} />;
  };

  /** ROWS & TOTAL_ROWS_COUNT */
  const rows = GetRows(data?.list, actionsComponent, modalBtnComponent, true);
  const totalRowsCount = data?.meta.count
    ? parseInt(data.meta.count, 10)
    : CommonConstants.INITIAL_ROWS_PER_PAGE;

  /** CALLS PRINT FUNCTION WHEN SCRIPT ID IS SET (PRINT TRIGGERED FROM ACTIONS MENU) */
  useEffect(() => {
    if (scriptId) {
      handlePrint();
    }
  }, [scriptId, handlePrint]);

  /** Handle Bulk Count
   * Callback to update ItemsCount
   */
  const handleBulk = (count: number) => setItemsCount(count);

  return !isLoading ? (
    <>
      <ScrollToTop
        dependencies={[
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) || '',
          query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) || '',
        ]}
      />
      <PrintLayout
        ref={componentRef}
        printData={{
          name: getResponseData(data?.list, scriptId)?.name,
          listType: getResponseData(data?.list, scriptId)?.type,
          isDefault: getResponseData(data?.list, scriptId)?.default,
          body: getResponseData(data?.list, scriptId)?.content,
        }}
      />
      <ScriptsModal
        isAdmin
        scriptId={scriptId}
        setScriptId={setScriptId}
        crudType={modalCrudType}
        setCrudType={setModalCrudType}
        deleteBtnOnClick={() => handleDelete([scriptId])}
      />
      <ConfirmationMessageModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setIsConfirmationModalOpen}
        message={getConfirmationMessageNode(confirmationActionType)}
        onClick={handleConfirmationModalClick}
      />
      <ScriptsTable
        isAdmin
        heading={pageHeading}
        tabChild={!!query.get(CommonConstants.QUERY_PARAMS.USERS.USER_ID)}
        selectedBtnNumber={itemsCount || checked.length}
        alertState={alertState}
        scriptType={DialerTypes.Active}
        deleteBtnProps={{
          onClick: () => {
            setConfirmationActionType(ConfirmationActionTypes.Delete);
            setIsConfirmationModalOpen(true);
            setIsBulkAction(true);
          },
          disabled: loading,
        }}
        scriptsAddBtnProps={{
          onClick: handleScriptAdd,
        }}
        tableProps={{
          loading,
          rows,
          checked,
          setChecked,
          paginationProps: {
            count: totalRowsCount,
          },
        }}
        handleBulk={handleBulk}
      />
    </>
  ) : (
    <FullScreenLoader />
  );
};

export default AdminScriptsActive;
